import Terminal, { TerminalTransactionResponse } from 'app/src/payments/terminals/Terminal';
import { TERMINAL_TYPE } from 'shared_modules/Terminals';
import Payment from 'app/src/data/models/Payment';
import SalePayment from 'app/src/data/models/SalePayment';
import PaymentTransaction from 'app/src/data/models/PaymentTransaction';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import CommonData from 'app/src/data/CommonData';
import Monitoring from 'shared_modules/Monitoring';
import { morphism } from 'morphism';
import $ from 'jquery';
import get from 'lodash/get';
import uuid from 'uuid/v4';
import axios from 'axios';
import URLHelper from 'app/src/data/models/utils/URLHelper';

const _MINIMUM_POLLING_INTERVAL = 3000; // Number of ms between polling interval
const _POLLING_BACKOFF_CONSTANT = 12;
export const _POLLING_WARNING_THRESHOLD = 2; // Number of polls before warning about degraded
export const _POLLING_ERROR_THRESHOLD = 15000; // Number of ms before we tell the merchant something is wrong after the warning happened

/**
 * Enum used by the CustomEvent for polling
 */
export const POLLING_EVENT = {
  NAME: 'onPollingStatusUpdate',
  STATUS: {
    DEGRADED: 'degraded',
    KO: 'ko',
    OK: 'ok',
  },
};

/**
 * Enum used by the monitoring
 */
export const EVENT_TYPES = {
  PAYMENT: {
    COMPLETED: 'payment.completed',
    STAGED: 'payment.staged',
    STAGING: 'payment.staging',
    STARTED: 'payment.started',
    TERMINAL: {
      DISCOVERING: 'payment.terminal.discovering',
      DISCOVERED: 'payment.terminal.discovered',
      CONNECTING: 'payment.terminal.connecting',
      CONNECTED: 'payment.terminal.connected',
      COLLECTING: 'payment.terminal.collecting',
      COLLECTED: 'payment.terminal.collected',
      PROCESSING: 'payment.terminal.processing',
      PROCESSED: 'payment.terminal.processed',
      CAPTURED: 'payment.terminal.captured',
    },
    POLLING: {
      [POLLING_EVENT.STATUS.DEGRADED]: 'payment.polling.degraded',
      [POLLING_EVENT.STATUS.KO]: 'payment.polling.ko',
    },
  },
};

const schemaP400 = {
  pTerminalId: 'id',
  pTerminalLocation: 'location',
  pTerminalIp: 'ip_address',
  pTerminalFirmware: 'device_sw_version',
};

export class TerminalEventMonitoring {
  constructor(ccTerminal, source) {
    this._source = source || null;
    this._uniqueClientTxId = uuid();
    this._gatewayConfigId = get(ccTerminal, 'gatewayConfigID', null);
    this._ccTerminalId = get(ccTerminal, 'ccTerminalID', null);
    this._terminalSerialNumber = get(ccTerminal, 'configuration.serialNumber', null);
    this._paymentId = null;
    this._paymentStatus = null;
    this._terminal = null;
    this._start = Date.now();
    this._last = this._start;

    // The following values can be 'unknown'
    this._terminalType = get(ccTerminal, 'type', 'unknown');
    this._terminalModelNumber = get(ccTerminal, 'configuration.modelNumber', 'unknown');
  }

  /**
   * getter paymentStatus
   */
  get paymentStatus() {
    return this._paymentStatus;
  }

  /**
   * setter paymentStatus
   *
   * @param {String} paymentStatus
   */
  set paymentStatus(paymentStatus) {
    this._paymentStatus = paymentStatus;
  }

  /**
   * getter terminal
   */
  get terminal() {
    return this._terminal;
  }

  /**
   * setter terminal
   *
   * @param {Object} terminalRaw data object from the terminal information
   */
  set terminal(terminalRaw) {
    switch (this._terminalType) {
      case TERMINAL_TYPE.LIGHTSPEED_P400_PLUS_STRIPE:
      case TERMINAL_TYPE.LIGHTSPEED_BBPOS_WISEPOSE_STRIPE:
        this._terminal = morphism(schemaP400, terminalRaw);
        break;
      default:
        this._terminal = {};
    }
  }

  /**
   * getter paymentId
   */
  get paymentId() {
    return this._paymentId;
  }

  /**
   * setter paymentId
   *
   * @param {String} paymentId
   */
  set paymentId(paymentId) {
    this._paymentId = paymentId;
  }

  /**
   * Abstract the NREvent monitoring from
   * the monitoring helper
   *
   * @param {String} eventName
   */
  submitEvent(eventName) {
    const now = Date.now();

    Monitoring.NREvent(eventName, this._source, {
      uniqueClientTxId: this._uniqueClientTxId,
      gatewayConfigId: this._gatewayConfigId,
      ccTerminalId: this._ccTerminalId,
      terminalType: this._terminalType,
      terminalModelNumber: this._terminalModelNumber,
      terminalSerialNumber: this._terminalSerialNumber,
      paymentId: this._paymentId,
      paymentStatus: this._paymentStatus,
      msSinceStart: now - this._start,
      msSinceLast: now - this._last,
      ...this._terminal,
    });

    this._last = now;
  }

  /**
   * Abstract the NRError monitoring from
   * the monitoring helper
   *
   * @param {Error} error - Javascript Error object
   * @param {String} code - error code
   */
  submitError(error, code = null) {
    Monitoring.NRError(error, this._source, {
      uniqueClientTxId: this._uniqueClientTxId,
      gatewayConfigId: this._gatewayConfigId,
      ccTerminalId: this._ccTerminalId,
      terminalType: this._terminalType,
      terminalModelNumber: this._terminalModelNumber,
      terminalSerialNumber: this._terminalSerialNumber,
      paymentId: this._paymentId,
      paymentStatus: this._paymentStatus,
      errorCode: code || error.code,
      reason: error.reason || null,
      ...this._terminal,
    });
  }
}

export default class PaymentServiceTerminal extends Terminal {
  constructor(terminalModel, pollingInterval = _MINIMUM_POLLING_INTERVAL) {
    super(terminalModel);

    this._pollingTimer = null;
    this._elapsedTime = 0;
    this._missedPollCount = 0;
    this._errorPollingTimeout = null;
    this.terminalEventMonitoring = null;
    this.pollingInterval = pollingInterval;
  }

  get missedPollCount() {
    return this._missedPollCount;
  }

  set missedPollCount(count = 0) {
    this._missedPollCount = count;
  }

  /**
   * This method restore all the timers and the polling count
   */
  reset() {
    if (this.missedPollCount > 0) {
      this.missedPollCount = 0;
    }

    if (this._errorPollingTimeout) {
      // Reset and clear the timers
      clearTimeout(this._errorPollingTimeout);
      this._errorPollingTimeout = null;
    }

    if (this._pollingTimer) {
      // Reset and clear the timers
      clearTimeout(this._pollingTimer);
      this._pollingTimer = null;
    }
  }

  /**
   * Creates the appropriate payment transaction structure for a pending payment.  We'll use
   * that to attempt to recover a pending transaction.
   *
   * @param {Payment} recoveredPayment The payment for which we want to attempt to continue
   * @param {GatewayConfig} gatewayConfig The gateway configuration used to transact the request
   */
  getRecoveredTransaction(payment, gatewayConfig) {
    // store the last transaction that was created; used to obtain details about the last
    // transaction that was initiated with this terminal
    this.lastPayment = payment;

    let terminalPayment = this._createPayment(payment.get('totalAmount'));
    let paymentTransaction = new PaymentTransaction({
      paymentTransactionID: payment.nextPaymentTransactionID,
      gateway: gatewayConfig.get('gatewayID'),
      paymentID: payment.id,
      shopID: CommonData.selectedShop.id,
      registerID: CommonData.selectedRegister.id,
      terminalID: this.model.id,
    });

    paymentTransaction.gatewayRequest = payment.terminalApiUrl;

    let salePayment = payment.getRelation('SalePayment', SalePayment);
    if (salePayment) {
      terminalPayment.model.set(salePayment.attributes);
    }

    return { payment, terminalPayment, paymentTransaction };
  }

  /**
   * Communicates with the terminal to start a transaction.
   *
   * @param {Object} paymentOptions
   * @param {Decimal} paymentOptions.amount  The amount to transact with the terminal.
   * @param {Decimal} paymentOptions.tipAmount  The tipAmount to transact with the terminal.
   * @param {Number} paymentOptions.saleID   The ID of the sale to which the transaction will be associated
   * @param {GatewayConfig} paymentOptions.gatewayConfig  The gateway configuration used to transact the request
   * @param {Boolean} paymentOptions.isManualEntry  When true, the terminal will request for manual card entry
   * @param {Payment} paymentOptions.recoveredPayment Optional - When specified, the system will attempt to
   *                                                  continue the specified payment. This can occurr if there
   *                                                  is a pending payment in the sale that was unresolved.
   * @param {String} paymentOptions.refundedPaymentId Optional - When specified, the system will attempt to
   *                                                  perform a refund via the terminal (currently only Stripe)
   * @param {String} paymentOptions.workflow Optional - Override for the payment workflow
   */
  stagePayment({
    amount,
    saleID,
    gatewayConfig,
    isManualEntry = false,
    tipAmount = null,
    recoveredPayment = null,
    refundedPaymentID = null,
    cardToken = null,
    workflow = Payment.Workflow.TERMINAL_PAYMENTS,
  }) {
    const shopID = CommonData.selectedShop.id;
    const registerID = CommonData.selectedRegister.id;

    if (shopID && registerID) {
      let deferred = $.Deferred();
      if (saleID && gatewayConfig?.get('gatewayID')) {
        if (recoveredPayment) {
          deferred.resolve(this.getRecoveredTransaction(recoveredPayment, gatewayConfig));
        } else {
          let payment = new Payment({
            method: Payment.Methods.BANK_CARD,
            totalAmount: amount,
            saleID,
            // TODO: Eventually the payment service will take care of setting the payment type
            // but for now just set it to the credit card type
            paymentTypeID: PaymentTypeManager.getPaymentIdForType(
              PaymentTypeManager.Types.TERMINAL
            ),
            shopID,
            registerID,
            ...(tipAmount && { tipAmount }),
          });

          payment.gateway = gatewayConfig.get('gatewayID');
          payment.terminalConfigID = this.model.id;
          payment.workflow = workflow;
          payment.isManualEntryAllowed = isManualEntry;
          payment.refundedPaymentId = refundedPaymentID;
          payment.refundCardToken = cardToken;

          if (isManualEntry) {
            // put the terminal (when available) into manual entry mode
            payment.invokeManualEntry = true;
          }

          // POST on the payment obtains the forged URL to initiate the transaction and creates
          // a SalePayment against the sale.
          payment
            .save()
            .done(() => {
              // store the last transaction that was created; used to obtain details about the last
              // transaction that was initiated with this terminal
              this.lastPayment = payment;

              let terminalPayment = this._createPayment(amount);
              let paymentTransaction = new PaymentTransaction({
                paymentTransactionID: payment.nextPaymentTransactionID,
                gateway: gatewayConfig.get('gatewayID'),
                paymentID: payment.id,
                shopID: CommonData.selectedShop.id,
                registerID: CommonData.selectedRegister.id,
                terminalID: this.model.id,
              });

              paymentTransaction.gatewayRequest = payment.terminalApiUrl;

              let salePayment = payment.getRelation('SalePayment', SalePayment);
              if (salePayment) {
                terminalPayment.model.set(salePayment.attributes);
              }

              deferred.resolve({ payment, terminalPayment, paymentTransaction });
            })
            .fail(response => {
              // pass along the API-reported error without the file and line number
              let error = get(response, 'responseJSON.message', '').replace(/ *\([^)]*\) */g, '');

              deferred.reject(
                new TerminalTransactionResponse(
                  Terminal.Response.ERROR,
                  Terminal.Status.ERROR,
                  null,
                  error
                )
              );
            });
        }
      } else {
        const message = `A sale ID, gateway ID and terminalConfigID is required to process a payment for a Payment Service Terminal`;

        new TerminalEventMonitoring(this.model.attributes, { saleID }).submitError(
          new Error(message)
        );

        return deferred.reject(
          new TerminalTransactionResponse(
            Terminal.Response.ERROR,
            Terminal.Status.ERROR,
            null,
            message
          )
        );
      }

      return deferred.promise();
    }

    flashNotifier('warning', Localize.translate('There is a issue with your Shop Setup'), 4);
  }

  /**
   * Polls the status of the specified payment at a regular interval.  Once the status is no longer
   * pending the promise will be resolved or rejected accordingly.
   *
   * @param {Payment} payment     The payment who's status is currently unknown
   * @returns {*}
   */
  pollApiForPaymentStatus(payment) {
    let deferred = $.Deferred();

    this._elapsedTime = 0;
    if (this._pollingTimer) {
      clearTimeout(this._pollingTimer);
    }

    this._getPaymentStatus(payment, deferred);

    return deferred.promise();
  }

  /**
   * Generates the duration before the next poll for a payment transaction status.  Delegate method
   * invoked by the pollApiForPaymentStatus() method.
   *
   * @param {Number} elapsedTime      The elapsed time in milliseconds
   */
  nextPollingInterval(elapsedTime = 0) {
    // time to next retry = max(3, elapsed time in seconds / 12) seconds
    return Math.max(this.pollingInterval, elapsedTime / _POLLING_BACKOFF_CONSTANT);
  }

  /**
   * Dispatches the proper event when the different thresholds
   * _POLLING_WARNING_THRESHOLD and _POLLING_ERROR_THRESHOLD
   * are reached.
   */
  badPoll() {
    if (this.missedPollCount <= _POLLING_WARNING_THRESHOLD) {
      // eslint-disable-next-line operator-assignment
      this.missedPollCount = this.missedPollCount + 1;
    }

    if (this.missedPollCount === _POLLING_WARNING_THRESHOLD) {
      Monitoring.NRError(
        EVENT_TYPES.PAYMENT.POLLING[POLLING_EVENT.STATUS.DEGRADED],
        'PaymentServiceTerminal'
      );
      window.dispatchEvent(
        new CustomEvent(POLLING_EVENT.NAME, { detail: POLLING_EVENT.STATUS.DEGRADED })
      );

      if (!this._errorPollingTimeout) {
        this._errorPollingTimeout = setTimeout(() => {
          Monitoring.NRError(
            EVENT_TYPES.PAYMENT.POLLING[POLLING_EVENT.STATUS.KO],
            'PaymentServiceTerminal'
          );
          window.dispatchEvent(
            new CustomEvent(POLLING_EVENT.NAME, { detail: POLLING_EVENT.STATUS.KO })
          );

          clearTimeout(this._errorPollingTimeout);
        }, _POLLING_ERROR_THRESHOLD);
      }
    }
  }

  /**
   * Dispatches the proper event when receive a good
   * poll after a series of bad ones. It also reset the
   * missedPollCount to 0
   */
  goodPoll() {
    // Reset the missed poll count since this query worked
    if (this.missedPollCount > 0) {
      this.missedPollCount = 0;

      if (this._errorPollingTimeout) {
        // Reset and clear the timers
        clearTimeout(this._errorPollingTimeout);
        this._errorPollingTimeout = null;
      }

      window.dispatchEvent(
        new CustomEvent(POLLING_EVENT.NAME, { detail: POLLING_EVENT.STATUS.OK })
      );
    }
  }

  /**
   * Initiate the setTimeout for the polling that will calls
   * the update of the payment status at every nextPollingInterval
   *
   * @param {Payment} payment
   * @param {JQueryDeferred} deferred promise
   */
  _keepPolling(payment, deferred) {
    // We opt to use setTimeout rather than setInterval since we
    // want to avoid the potential case where the fetch from the API takes longer
    // than the polling interval.
    if (this._pollingTimer) {
      clearTimeout(this._pollingTimer);
    }

    this._pollingTimer = setTimeout(() => {
      this._elapsedTime += this.nextPollingInterval(this._elapsedTime);
      this._getPaymentStatus(payment, deferred);
    }, this.nextPollingInterval(this._elapsedTime));
  }

  /**
   * Helper function to retrieve the status of the payment from the API.  Once the status is no longer
   * pending the promise will be resolved or rejected accordingly.
   * @param {Payment} payment     The payment who's status is currently unknown
   * @param {Deferred} deferred   The deferred object that _pollApiForPaymentStatus uses to determine
   *                              when the status of the payment has been resolved.
   * @private
   */
  _getPaymentStatus(payment, deferred) {
    payment
      .fetch()
      .done(() => {
        this.goodPoll();

        switch (payment.get('status')) {
          case Payment.Status.APPROVED:
            deferred.resolve();
            delete this._pollingTimer;
            break;

          case Payment.Status.PENDING:
            // Keep polling.  We opt to use setTimeout rather than setInterval since we
            // want to avoid the potential case where the fetch from the API takes longer
            // than the polling interval.
            this._keepPolling(payment, deferred);
            break;

          default:
            deferred.reject();
            delete this._pollingTimer;
            break;
        }
      })
      .fail(() => {
        this.badPoll();
        this._keepPolling(payment, deferred);
      });
  }

  /**
   * Cancels payment at the Pay-Service level
   *
   * @returns a Promise that resolves to an empty object once the command has been successfully canceled.
   * @return {error} If cancellation fails, the Promise resolves to an object with an error
   */
  cancelPayment(payment) {
    let deferred = $.Deferred();

    const event = new CustomEvent(Terminal.TerminalEvents.CANCEL_STARTED, {
      detail: { disableCancelButton: true },
    });
    window.dispatchEvent(event);
    axios
      .post(`${URLHelper.getAccountURL('/Payment')}/${payment.id}/Cancel.json`, {})
      .then(() => {
        deferred.resolve();
      })
      .catch(error => {
        const { response = {} } = error || {};
        if (response.status === 424) {
          deferred.reject(
            new TerminalTransactionResponse(
              Terminal.Response.CANCEL_UNAVAILABLE,
              Terminal.Status.ERROR,
              null,
              error
            )
          );
        } else {
          deferred.reject(
            new Error(
              `Something went wrong while trying to abort the payment. If the problem persists please contact support.`
            )
          );
        }
      });

    return deferred.promise();
  }
}
